import React, { useCallback, useMemo } from 'react';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { IconChevronRight } from '@wds/icons';

import { COMPARISON_PACKAGES } from '~app/pages/Pricing/lib/constants';
import { Packages } from '~shared/constants/pricing';
import { PackageData } from '~shared/types/pricing/package';
import { PACKAGE_SWAP } from '../../constants';

import useDiscountStyles from './useStyles';

import { getCoreCostFromSkus, getPackageType } from '~app/pages/Pricing/lib/lib-helpers';

import { usePricingState } from '~shared/hooks/usePricingState';
import { switchAndSave } from '../../helpers';
import useTrackingDetails from '~app/pages/Pricing/hooks/useTrackingDetails';
import { PackageHeaderProps } from '~app/pages/Pricing/components/Summary/types';
import COPY from '../../copy/experimentPackageCopy';
import ACTIONS from '~shared/actions';
import { CombinedActions } from '~shared/types/pricing/actions';
import { TranslationsPlaceHolderType } from '~app/pages/Pricing/types';
import { logAmplitudeEvent } from '~app/pages/Pricing/pages/shared';
import { AMPLITUDE_PAGE_EVENTS } from '~shared/util';

const EXP_COMPARISON_PACKAGES = {
  ...COMPARISON_PACKAGES,
  [Packages.AdvantageAnnual]: Packages.TeamAdvantage,
  [Packages.PremierAnnual]: Packages.TeamPremier,
};

const TEAMS_NUM_USERS = '3';

export default function DiscountByline({ packageName, skuCosts }: Readonly<PackageHeaderProps>): JSX.Element {
  const { Discount, DiscountApplied, SwitchForDiscount } = useDiscountStyles();
  const { pageName } = useTrackingDetails();

  const comparisonPackageName = EXP_COMPARISON_PACKAGES[packageName];

  const {
    state: {
      packages: { allPackages },
      grow2792: { advantageUsers, premierUsers },
    },
    dispatch,
  } = usePricingState();

  const { isAdvantagePackage, isPremierPackage } = getPackageType(packageName);

  const coreSeatCost = useMemo(() => getCoreCostFromSkus(skuCosts), [skuCosts]);

  const comparisonPackage = useMemo(
    () =>
      comparisonPackageName
        ? allPackages?.find((packageObj: PackageData) => packageObj.packageName === comparisonPackageName)
        : null,
    [comparisonPackageName, allPackages]
  );

  const comparisonSeatCost = useMemo(
    () => (comparisonPackage ? getCoreCostFromSkus(comparisonPackage?.skuCost) : null),
    [comparisonPackage]
  );

  const savingsPercentage = useMemo(() => {
    let annualPackage = null;
    let monthlyPackage = null;

    switch (packageName) {
      case Packages.FormsAnnual:
      case Packages.FormsMonthly:
        annualPackage = allPackages.find(pkg => pkg.packageName === Packages.FormsAnnual);
        monthlyPackage = allPackages.find(pkg => pkg.packageName === Packages.FormsMonthly);
        break;

      case Packages.TeamAdvantage:
      case Packages.AdvantageAnnual:
      case Packages.AdvantageMonthly:
        annualPackage = allPackages.find(pkg => pkg.packageName === Packages.AdvantageAnnual);
        monthlyPackage = allPackages.find(pkg => pkg.packageName === Packages.AdvantageMonthly);
        break;
    }

    let percentage = 0;

    if (annualPackage && monthlyPackage) {
      const annualSkuCost = getCoreCostFromSkus(annualPackage.skuCost);
      const monthlySkuCost = getCoreCostFromSkus(monthlyPackage.skuCost);

      if (annualSkuCost && monthlySkuCost) {
        percentage = (annualSkuCost.cost / (monthlySkuCost.cost * 12)) * 100 - 100;
      }
    }

    return Math.floor(-percentage);
  }, [allPackages, packageName]);

  const seatSavingsPercentage = useMemo(() => {
    let percentage = 0;

    if (comparisonPackage && coreSeatCost && comparisonSeatCost) {
      const costDifference = Math.abs(comparisonSeatCost?.cost - coreSeatCost?.cost);
      const equation =
        packageName === Packages.TeamAdvantage || packageName === Packages.TeamPremier
          ? costDifference / comparisonSeatCost?.cost
          : costDifference / coreSeatCost?.cost;
      percentage = Math.floor(equation * 100);
    }

    return percentage;
  }, [comparisonPackage, comparisonSeatCost, coreSeatCost, packageName]);

  const handleClick = useCallback(() => {
    if ([Packages.AdvantageAnnual, Packages.PremierAnnual].includes(packageName)) {
      switchAndSave({ numberOfUsers: TEAMS_NUM_USERS, isAdvantagePackage, isPremierPackage, pageName, dispatch });
    }

    if ([Packages.FormsMonthly, Packages.AdvantageMonthly].includes(packageName)) {
      const packageSwap = {
        packageToBeReplaced: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].id,
        replacedWith: PACKAGE_SWAP[packageName as keyof typeof PACKAGE_SWAP].swap.id,
      };
      dispatch({ type: ACTIONS.SWITCH_PACKAGES, payload: packageSwap } as CombinedActions);

      logAmplitudeEvent({
        eventName: 'pricing clicked Save with annual plan button',
        pageName,
        amplitudeEvent: AMPLITUDE_PAGE_EVENTS.experimentEvent,
        otherData: {
          packageName,
        },
      });
    }
  }, [dispatch, isAdvantagePackage, isPremierPackage, packageName, pageName]);

  const getSavings = (): JSX.Element => {
    if ((isAdvantagePackage && advantageUsers > 1) || (isPremierPackage && premierUsers > 1)) {
      return <h4 className={`${Discount} ${DiscountApplied}`}>{seatSavingsPercentage}% multi-user discount applied</h4>;
    }

    const Arrow = <IconChevronRight title="Right arrow" role="presentation" />;

    let btnCopy = t(COPY.SAVE_MORE_WITH_TEAMS, {
      percentage: seatSavingsPercentage,
      group: ({ children }: TranslationsPlaceHolderType) => (
        <span>
          {children} {Arrow}
        </span>
      ),
    });

    if ([Packages.FormsMonthly, Packages.AdvantageMonthly].includes(packageName)) {
      btnCopy = t(COPY.SAVE_WITH_ANNUAL, {
        percentage: savingsPercentage,
        group: ({ children }: TranslationsPlaceHolderType) => (
          <span>
            {children} {Arrow}
          </span>
        ),
      });
    }

    return (
      <Box className={`${Discount} ${SwitchForDiscount}`}>
        <Button variant="text" color="link" size="md" onClick={handleClick}>
          {btnCopy}
        </Button>
      </Box>
    );
  };

  return getSavings();
}
